// --- Core
import {
    LOW_BG_CLASS,
    MED_BG_CLASS,
    HIGH_BG_CLASS,
    LOW_TEXT_CLASS,
    MED_TEXT_CLASS,
    HIGH_TEXT_CLASS,
    default as DsHelper,
} from './DsHelper.js';

/**
 * Returns the database column of the `sos_fpa` table for the rank against
 * a given fantasy position
 * @param {string} fantasyPosition
 * @returns {string|boolean}
 */
export const getAgainstField = fantasyPosition => {
    switch (fantasyPosition) {
        case 'QB':
        case 'RB':
        case 'WR':
        case 'TE':
        case 'K':
        case 'DEF':
            return 'against_' + fantasyPosition.toLowerCase();
        case 'DB':
        case 'LB':
        case 'DL':
        case 'IDP FLEX':
            return 'against_def';
        case 'TQB':
            return 'against_qb';
        case 'TK':
            return 'against_k';
    }
    return false;
};

/**
 * Returns the database column of the `sos_fpa` table for the percentage difference
 * against a given fantasy position
 * @param {string} fantasyPosition
 * @returns {string|boolean}
 */
export const getAgainstPercentageField = fantasyPosition => {
    switch (fantasyPosition) {
        case 'QB':
        case 'RB':
        case 'WR':
        case 'TE':
        case 'K':
        case 'DEF':
            return 'against_' + fantasyPosition.toLowerCase() + '_percent_diff';
        case 'DB':
        case 'LB':
        case 'DL':
        case 'IDP FLEX':
            return 'against_def_percent_diff';
        case 'TQB':
            return 'against_qb_percent_diff';
        case 'TK':
            return 'against_k_percent_diff';
    }
    return false;
};

/**
 * Gets the 'level' determined by DS as having a SOS against_* field value
 * being low, med or high
 * @param string val The value of an 'against_*_percent_diff' field
 * @return string
 */
export const getAgainstLevel = function (val) {
    if (val < -0.1) {
        return DsHelper.LOW;
    } else if (val > 0.1) {
        return DsHelper.HIGH;
    }
    return DsHelper.MED;
};

/**
 * Gets the CSS class for a strength of schedule against_*_percent_diff field
 * @param {number} sosPercentDiff The value of an 'against_*_percent_diff' field
 * @returns {string}
 */
export const getSosClass = function (sosPercentDiff) {
    const level = getAgainstLevel(sosPercentDiff);
    if (level == DsHelper.LOW) {
        return LOW_BG_CLASS;
    }
    if (level == DsHelper.HIGH) {
        return HIGH_BG_CLASS;
    }
    return MED_BG_CLASS;
};

/**
 * Gets the CSS class used to color text to illustrate the level of the SOS
 * against a certain position / roster spot.
 * @param {number} value The value of an 'against_*_percent_diff' field
 * @returns {string}
 */
export const getSosTextClass = function (value) {
    let level = getAgainstLevel(value);
    return level == DsHelper.LOW ? LOW_TEXT_CLASS : level == DsHelper.HIGH ? HIGH_TEXT_CLASS : MED_TEXT_CLASS;
};

/**
 * @param {number} avgRemainingSos The average remaining strength of schedule
 * determined by averaging the `against_*_percent_diff` field for a team's
 * opponents for a chunk (normally the remaining games) in their schedule.
 * @return {string}
 */
export const getAvgRemainingSosLevel = avgReminingSos => {
    if (avgReminingSos < -0.02) {
        return DsHelper.LOW;
    } else if (avgReminingSos > 0.02) {
        return DsHelper.HIGH;
    }
    return DsHelper.MED;
};

/**
 * Gets the CSS class to use to illustrate the level of the averaged remaining
 * strength of schedule for a team against a certain position / roster spot.
 * @param {number} avgRemainingSos The average remaining strength of schedule
 * determined by averaging the `against_*_percent_diff` field for a team's
 * opponents for a chunk (normally the remaining games) in their schedule.
 * @returns {string}
 */
export const getAvgRemainingSosBgClass = function (avgReminingSos) {
    const level = getAvgRemainingSosLevel(avgReminingSos);
    if (level == DsHelper.LOW) {
        return LOW_BG_CLASS;
    }
    if (level == DsHelper.HIGH) {
        return HIGH_BG_CLASS;
    }
    return MED_BG_CLASS;
};


/**
 * Returns the HTML for a pill for average remaining SOS with the proper
 * class for the bg color.
 * @param {float} avgRemainingSos
 * @returns {string}
 */
export const getAvgRemainingSosPill = (avgRemainingSos) => {
    const cssClass = getAvgRemainingSosBgClass(avgRemainingSos);
    const displayText = avgRemainingSos ? (avgRemainingSos * 100).toFixed(1) + '%' : '-';
    return '<span class="ds-pill ' + cssClass + '">' + displayText + '</span>';
}

/**
 * Gets the CSS class used to color text to illustrate the level of the averaged
 * remaining SOS for a team against a certain position / roster spot.
 * @param {number} avgRemainingSos The average remaining strength of schedule
 * determined by averaging the `against_*_percent_diff` field for a team's
 * opponents for a chunk (normally the remaining games) in their schedule.
 * @returns {string}
 */
export const getAvgRemainingSosTextClass = function (avgReminingSos) {
    const level = getAvgRemainingSosLevel(avgReminingSos);
    if (level == DsHelper.LOW) {
        return LOW_TEXT_CLASS;
    }
    if (level == DsHelper.HIGH) {
        return HIGH_TEXT_CLASS;
    }
    return MED_TEXT_CLASS;
};

/**
 * Returns the value of an `sos_fpa` record's `against_X` column for a team.
 * Since this is used in many scenarios this funciton accepts a very flexible
 * `data` parameter can have a variety of proeprties to help fget this value.
 * @param {String} fantasyPosition The position we are figuring out perofrmance
 * metrics for against the given team
 * @param {object} data The data to figure out the value. May have one of several
 * properties to help retrieve the needed value
 * @property {object} [data.sosFpaRecord] The `sos_fpa` record on which the
 * `against_X` property value we're looking for is found. If this is provided
 * no other properties are required to determine the value.
 * @property {array} [data.sosFpaRecords] An array of all `sos_fpa_records` for
 * (normally) the current year. If provided instead of `sosFpaRecord`, other
 * properties are also required to determine the team whose `sos_fpa` record
 * we are looking for.
 * @property {object} [data.scheduleRecord] The `schedule` record from which the
 * `opponent_team_id` may be used to find the appropriate `sos_fpa` record from
 * `data.sosFpaRecords`. Either this or a combination of `data.scheduleRecordsForWeek`
 * and `data.scheduleTeamId` are required.
 * @property {array} [data.scheduleRecordsForWeek]  An array of `schedule` records
 * for a given week. If this is provided, `data.scheduleTeamId` is also 
 * required to help find the appropriate record from this array.
 * @property {number} [data.scheduleTeamId] The `team_id` value for the
 * `schedule` record to use.
 * @property {boolean|string} [data.returnValueIfNotFound] What to return if the
 * desired value cannot be found. This may be utilized if, for example, we are
 * looking for a value for a player whose team has a bye week and no opponent to
 * get an `against_X` value for.
 * @property {number} [data.toFixed] The number of decimals to round to in the
 * returned value. Defaults to 1.
 * @property {string} [data.returnFormat] The format to return a value in. May
 * be "raw" for the raw column value, "percent" for that value multiplied by 100
 * with a "%" symbol appended, or "pill" for the HTML for a pill witht he 
 * appropriate background color
 * @returns {Number|String}
 */
export const getPerformanceForPositionAgainstTeam = function (fantasyPosition, data) {
    let {
        sosFpaRecord,
        sosFpaRecords,
        scheduleRecord,
        scheduleRecordsForWeek,
        scheduleTeamId,
        returnValueIfNotFound,
        toFixed,
        returnFormat
    } = {
        sosFpaRecord: null,
        sosFpaRecords: [],
        scheduleRecord: null,
        scheduleRecordsForWeek: [],
        scheduleTeamId: false,
        returnValueIfNotFound: false,
        toFixed: 1,
        returnFormat: "raw",
        ...data
    }

    // --- Get the `against_X` field name first since some positions don't even
    // --- have a value. If we don't, return our configured not found value.
    const againstPercentageField = getAgainstPercentageField(fantasyPosition);
    if (!againstPercentageField) {
        return returnValueIfNotFound;
    }

    // --- If we have an `sos_fpa` record we just get the needed field and
    // --- return in the requested format
    if(sosFpaRecord){
        return getFormattedPerformanceForPositionAgainstTeam(
            sosFpaRecord[againstPercentageField],
            returnFormat,
            toFixed
        )
    }

    // --- If we weren't provided `sos_fpa` records then it's probably an error
    // --- so log that and return the 'not found' value
    if(!sosFpaRecords.length){
        console.error('No `sos_fpa` records were provided to get the performance of a position against a team', data);
        return returnValueIfNotFound;
    }

    // --- If we don't have the schedule data needed to find the apporpriate `sos_fpa`
    // --- record log that and return the default not found value.
    if(
        !scheduleRecord &&
        (scheduleRecordsForWeek.length == 0 || !scheduleTeamId)
    ) {
        console.error('Missing a schedule record, array of records for the week, or the `team_id` for the schedule record required to get the performance of a position against a team.', data);
        return returnValueIfNotFound;
    }

    // --- If we don't have a schedule record, try to find the one we need from the
    // --- array of records for the week. If there isn't one, it may be a bye week
    // --- in which case we appropriately return the not found value.
    if(!scheduleRecord){
        scheduleRecord = scheduleRecordsForWeek.find(scheduleRecordForWeek => scheduleRecordForWeek.team_id == scheduleTeamId);
        if(!scheduleRecord){
            return returnValueIfNotFound;
        }
    }

    // --- Get the `sos_fpa` record for the opponent of the provided `scheduleTeamId`
    sosFpaRecord = sosFpaRecords.find(sosFpaRecord => sosFpaRecord.team_id == scheduleRecord.opponent_team_id);
    if(!sosFpaRecord){
        // --- It's valid
        return returnValueIfNotFound;
    }

    return getFormattedPerformanceForPositionAgainstTeam(
        sosFpaRecord[againstPercentageField],
        returnFormat,
        toFixed
    )
}

/**
 * Returns a formatted value of an `against_X` column on an `sos_fpa` record.
 * @param {number} rawValue The raw value of an `against_X` column on an 
 * `sos_fpa` record
 * @param {string} format The format to return in, either "raw", "percent" or "pill"
 * @param {number} toFixed The number of decimals to return.
 * @returns {number|string}
 */
export const getFormattedPerformanceForPositionAgainstTeam = (rawValue, format, toFixed) => {
    if(format == 'raw'){
        return rawValue;
    }
    const percentValue = (rawValue * 100).toFixed(toFixed)+'%';
    if(format == 'percent'){
        return percentValue;
    }

    if(format == 'pill'){
        const classAttr = getSosClass(rawValue)
        return '<span class="ds-pill ' + classAttr + '">' + percentValue + '</span>'
    }

    throw new Error('Could not determine how to return performance for position against team for format "'+format+'"');
}

/**
 * StrengthOfScheduleHelper contains functions and variables for dealing with
 * `strength_of_schedule` records
 */
export default {
    getAgainstLevel,
    getAgainstPercentageField,
};
